import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCreditCard, FaCalendarAlt, FaDollarSign, FaCheckCircle, FaTimes, FaReceipt, FaEye, FaSpinner } from 'react-icons/fa';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/payment-history`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('pressToken')}` } }
      );
      if (res.data.success) setPayments(res.data.data);
      else setError(res.data.message || 'Failed to fetch payment history');
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError('Failed to fetch payment history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPaymentHistory(); }, []);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const formatAmount = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);

  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'captured': return { bg: 'bg-emerald-100 text-emerald-700', icon: FaCheckCircle, label: 'Success' };
      case 'pending': return { bg: 'bg-amber-100 text-amber-700', icon: FaSpinner, label: 'Pending' };
      case 'failed': return { bg: 'bg-red-100 text-red-700', icon: FaTimes, label: 'Failed' };
      default: return { bg: 'bg-slate-100 text-slate-600', icon: FaReceipt, label: status || 'Unknown' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
          {[0, 1, 2].map(i => <div key={i} className="h-20 bg-white rounded-2xl animate-pulse" />)}
        </div>
        {[0, 1, 2, 3].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] p-6 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-4">
          <FaTimes className="w-8 h-8" />
        </div>
        <h3 className="text-lg font-black text-slate-800 mb-2">Failed to load</h3>
        <p className="text-slate-600 mb-6">{error}</p>
        <button onClick={fetchPaymentHistory} className="bg-slate-800 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-700 active:scale-95 transition-all">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8">

      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
          <FaCreditCard className="text-blue-600" /> Payment History
        </h1>
        <p className="text-sm font-medium text-slate-500 mt-1">View all your paid conference transactions</p>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-50 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center">
              <FaReceipt className="text-blue-500 w-3 h-3" />
            </div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total</span>
          </div>
          <p className="text-xl font-black text-slate-800">{payments.length}</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-50 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center">
              <FaDollarSign className="text-emerald-500 w-3 h-3" />
            </div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Amount</span>
          </div>
          <p className="text-xl font-black text-slate-800 truncate">
            {formatAmount(payments.reduce((sum, p) => sum + (p.amount || 0), 0))}
          </p>
        </div>

        <div className="bg-white col-span-2 sm:col-span-1 rounded-2xl p-4 shadow-sm border border-slate-50 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-md bg-amber-50 flex items-center justify-center">
              <FaCheckCircle className="text-amber-500 w-3 h-3" />
            </div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Success</span>
          </div>
          <p className="text-xl font-black text-slate-800">
            {payments.filter(p => p.status === 'paid' || p.status === 'captured').length}
          </p>
        </div>
      </div>

      {/* ── Payment Cards Box ── */}
      {payments.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 flex flex-col items-center justify-center text-center shadow-sm border border-slate-50">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
            <FaReceipt className="w-6 h-6 text-slate-300" />
          </div>
          <p className="text-base font-black text-slate-700">No Payments Yet</p>
          <p className="text-sm text-slate-500 mt-1">You haven't made any payments for conferences.</p>
        </div>
      ) : (
        <div className="bg-white rounded-[1.5rem] shadow-sm border border-slate-100 p-2 sm:p-4 space-y-2">
          {payments.map((payment, index) => {
            const status = getStatusInfo(payment.status);
            return (
              <div key={payment._id || index}
                onClick={() => { setSelectedPayment(payment); setShowDetails(true); }}
                className="group bg-slate-50 hover:bg-slate-100 rounded-xl p-3 sm:p-4 flex items-center justify-between gap-3 cursor-pointer transition-colors border border-slate-100 hover:border-slate-200">

                {/* Left Side */}
                <div className="flex-1 min-w-0 flex items-center gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                    <status.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-black text-slate-800 truncate mb-0.5">
                      Conf: #{payment.conferenceId || 'N/A'}
                    </p>
                    <div className="flex items-center gap-2 text-[10px] sm:text-xs font-semibold text-slate-500 flex-wrap">
                      <span className="text-slate-400 font-mono">ID: {payment.paymentId ? payment.paymentId.substring(0, 12) + '...' : 'N/A'}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>{formatDate(payment.createdAt || payment.paymentDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Right Side */}
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className="text-sm sm:text-base font-black text-slate-800">{formatAmount(payment.amount)}</span>
                  <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-wider mt-1 flex items-center gap-1 ${status.bg} px-2 py-0.5 rounded-md`}>
                    {status.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Detail Bottom Sheet Modal ── */}
      {showDetails && selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowDetails(false)} />
          <div className="relative bg-white rounded-t-[2rem] sm:rounded-[2rem] w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl z-10 animate-slide-up sm:animate-fade-in">

            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                  <FaReceipt className="w-5 h-5" />
                </div>
                <button onClick={() => setShowDetails(false)} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-full flex items-center justify-center transition-colors">
                  <FaTimes />
                </button>
              </div>

              <h3 className="text-xl font-black text-slate-800 mb-1">Receipt</h3>
              <p className="text-xs font-semibold text-slate-400 mb-6">{formatDate(selectedPayment.createdAt || selectedPayment.paymentDate)}</p>

              <div className="bg-slate-50 rounded-2xl p-5 space-y-4 border border-slate-100">
                <div className="flex justify-between items-center pb-3 border-b border-slate-200/60">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Status</span>
                  <span className={`text-xs font-black px-2.5 py-1 rounded-md uppercase tracking-wider ${getStatusInfo(selectedPayment.status).bg}`}>
                    {getStatusInfo(selectedPayment.status).label}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-slate-500">Payment ID</span>
                  <span className="font-mono font-bold text-slate-700">{selectedPayment.paymentId || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-slate-500">Conference ID</span>
                  <span className="font-mono font-bold text-slate-700">#{selectedPayment.conferenceId || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-slate-500">Method</span>
                  <span className="font-bold text-slate-700 capitalize">{selectedPayment.method || 'N/A'}</span>
                </div>

                <div className="pt-3 border-t border-slate-200/60 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-semibold text-slate-500">Subtotal</span>
                    <span className="font-bold text-slate-700">{formatAmount(selectedPayment.subtotal || selectedPayment.amount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-semibold text-slate-500">GST ({selectedPayment.gstRate || 0}%)</span>
                    <span className="font-bold text-slate-700">{formatAmount(selectedPayment.gst || 0)}</span>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-200/60 flex justify-between items-center">
                  <span className="text-sm font-black text-slate-800 uppercase tracking-widest">Total</span>
                  <span className="text-xl font-black text-slate-800">{formatAmount(selectedPayment.amount)}</span>
                </div>
              </div>

              <div className="mt-6">
                <button onClick={() => setShowDetails(false)} className="w-full py-3.5 bg-slate-800 text-white font-black text-sm rounded-xl hover:bg-slate-700 active:scale-95 transition-all">
                  Close Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
